import React, { useState } from "react";
import {
  Container,
  Grid,
  Title,
  Text,
  Image,
  Stack,
  Group,
  Paper,
  Button,
  ActionIcon,
  Divider,
  Box,
  TextInput,
  Loader,
  rem,
  Center,
  Badge,
  Flex,
} from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";
import {
  IconTrash,
  IconPlus,
  IconMinus,
} from "@tabler/icons-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import apiService from "../services/api";
import { formatWeight } from "../utils/formatWeight";
import { formatPrice } from "../utils/formatPrice";
import { localizedField } from "../utils/localizedField";
import CartFreeOffersNotice from "../components/offers/CartFreeOffersNotice";
import AvailableOffers from "../components/offers/AvailableOffers";
import PriceBreakdown from "../components/PriceBreakdown";
import { getShippingDominantRate } from "../utils/shippingVat";

export default function Cart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    cartItems,
    couponDiscounts,
    ruleDiscount,
    ruleDiscountDescriptor,
    freeItems,
    availableOffers,
    shippingCost,
    shippingFree,
    volumeDiscount,
    volumeDiscountPercentage,
    appliedBalance,
    itemVat,
    itemsTotal,
    itemsRoundOffDiff,
    vatExclusive,
    netSubtotal,
    vatOnDiscount,
    loading,
    removeFromCart,
    updateQuantity,
    refreshCart,
  } = useCart();

  const [couponCode, setCouponCode] = useState("");
  const [couponFeedback, setCouponFeedback] = useState(null);
  const [isApplying, setIsApplying] = useState(false);

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    setIsApplying(true);
    try {
      await apiService.applyCoupon(couponCode);
      setCouponFeedback({
        type: "success",
        key: "couponApplied",
      });
      setCouponCode("");
      await refreshCart();
    } catch (err) {
      let key = "invalidCoupon";
      if (err.error_code === "ALREADY_USED") key = "couponAlreadyUsed";
      else if (err.error_code === "USAGE_LIMIT") key = "couponUsageLimitReached";
      setCouponFeedback({
        type: "error",
        key,
      });
    } finally {
      setIsApplying(false);
    }
  };

  if (!isAuthenticated)
    return (
      <Center py={100}>
        <Title order={2}>{t("loginRequired")}</Title>
      </Center>
    );
  if (loading)
    return (
      <Center py={100}>
        <Loader variant="dots" size="xl" />
      </Center>
    );
  if (cartItems.length === 0)
    return (
      <Center py={100}>
        <Title order={2}>{t("cartEmpty")}</Title>
      </Center>
    );

  return (
    <Box pb={rem(100)}>
      <Container size="lg" pt={rem(40)}>
        <Stack gap="xs" mb={rem(40)}>
          <Title order={1} size={rem(48)} fw={900} lts={-1}>
            {t("cartTitle", "Shopping Cart")}
          </Title>
          <Text c="dimmed" size="lg">
            {t("cartSubtitle", "Review your items before checkout")}
          </Text>
        </Stack>

        <CartFreeOffersNotice freeItems={freeItems} />
        <AvailableOffers offers={availableOffers} />

        <Grid gutter={rem(40)}>
          {/* ITEMS LIST */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {cartItems.map((item) => (
                <Paper
                    key={item.cartItemId}
                    withBorder
                    p="md"
                    radius="lg"
                    shadow="xs"
                  >
                    <Grid align="center" gutter="md">
                      <Grid.Col span={{ base: 3, sm: 3 }}>
                        <Image
                          src={item.image}
                          alt={localizedField(item, 'name', t.langCode)}
                          radius="md"
                          h={100} w={100} fit="cover"
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 9, sm: 9 }}>
                        <Flex
                          direction={{ base: "column", sm: "row" }}
                          justify={{ base: "flex-start", sm: "space-between" }}
                          align={{ base: "stretch", sm: "center" }}
                          gap={{ base: "sm", sm: "md" }}
                          wrap="nowrap"
                        >
                          <Stack gap={4} style={{ minWidth: 0 }}>
                            <Text fw={700} size="lg">
                              {localizedField(item, 'name', t.langCode)} - {item.weight_value != null ? formatWeight(item.weight_value, item.weight_unit) : null}
                            </Text>
                            <Group align="center" gap="xs" mt={5}>
                              <Text fw={800} color={item.hasSpecialPrice ? "red.7" : "blue.7"}>
                                {formatPrice(vatExclusive && item.netPrice != null ? item.netPrice : item.price)}
                              </Text>
                              {item.hasSpecialPrice && item.originalPrice && (
                                <Text size="sm" td="line-through" c="dimmed">
                                  {formatPrice(item.originalPrice * (vatExclusive && item.vatRate > 0 ? 1 / (1 + item.vatRate / 100) : 1))}
                                </Text>
                              )}
                              {item.hasSpecialPrice && (
                                <Badge color="green" variant="filled" size="xs">
                                  {t('yourPrice', 'Your Price')}
                                </Badge>
                              )}
                            </Group>
                            {item.vatRate !== undefined && (
                              <Text size="xs" c="dimmed" mt={-2}>
                                {vatExclusive ? t('excl', 'excl.') : t('incl', 'incl.')} {item.vatRate}% {t('vat', 'VAT')}
                              </Text>
                            )}
                            {item.hasSpecialPrice && item.discountPercentage != null && (
                              <Text size="xs" fw={600} color="green.7">
                                {t('youSave', 'You save:')} {item.discountPercentage}%
                              </Text>
                            )}
                            {Number(item.can_preorder) !== 0 && item.quantity > item.stock_quantity && (
                              <Stack gap={2} role="status">
                                <Text size="xs" c="orange.7" fw={800} tt="uppercase" lts={1}>
                                  {t('stockShortfallTitle', 'Guaranteed Fresh & Crafted for You')}
                                </Text>
                                <Text size="xs" c="orange.7" fw={600}>
                                  {t('stockShortfallNote', 'We make everything in small, exclusive batches to ensure the highest quality. Your order is secured for our next production run and will be shipped right after. Thank you for choosing fresh!')}
                                </Text>
                              </Stack>
                            )}
                          </Stack>
                        <Group wrap="nowrap" align="center" gap="sm" style={{ flexShrink: 0 }}>
                          <Group
                            gap={5}
                            wrap="nowrap"
                            role="group"
                            aria-label={t('cartQuantityGroup', `Quantity for ${localizedField(item, 'name', t.langCode) || 'item'}`)}
                            style={{
                              border: "1px solid #eee",
                              borderRadius: "4px",
                              padding: "4px",
                            }}
                          >
                            <ActionIcon
                              variant="subtle"
                              onClick={() =>
                                updateQuantity(item.cartItemId, item.quantity - 1)
                              }
                              disabled={item.quantity <= 1}
                              aria-label={t('decreaseQuantity', `Decrease quantity of ${localizedField(item, 'name', t.langCode) || 'item'}`)}
                            >
                              <IconMinus size={14} aria-hidden="true" />
                            </ActionIcon>
                            <Text fw={700} w={30} ta="center" aria-label={t('currentQuantityLabel', `Quantity: ${item.quantity}`)}>
                              {item.quantity}
                            </Text>
                            <ActionIcon
                              variant="subtle"
                              onClick={() =>
                                updateQuantity(item.cartItemId, item.quantity + 1)
                              }
                              disabled={Number(item.can_preorder) === 0 && item.quantity >= (item.stock_quantity ?? 0)}
                              aria-label={t('increaseQuantity', `Increase quantity of ${localizedField(item, 'name', t.langCode) || 'item'}`)}
                            >
                              <IconPlus size={14} aria-hidden="true" />
                            </ActionIcon>
                          </Group>
                          <ActionIcon
                            variant="light"
                            color="red"
                            radius="xl"
                            onClick={() => removeFromCart(item.cartItemId)}
                            aria-label={t('removeFromCart', `Remove ${localizedField(item, 'name', t.langCode) || 'item'} from cart`)}
                          >
                            <IconTrash size={18} aria-hidden="true" />
                          </ActionIcon>
                        </Group>
                      </Flex>
                    </Grid.Col>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </Grid.Col>

          {/* SUMMARY SIDEBAR */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="lg">
              <Paper p="xl" radius="lg" withBorder bg="gray.0" shadow="sm">
                <Title order={3} mb="xl" fw={800}>
                  {t("orderSummary", "Order Summary")}
                </Title>

                <Stack gap="sm">
                  {(() => {
                    // Engine amounts are NET. For retail (gross) display, gross-up
                    // NET values using the dominant VAT rate from cart items.
                    const dominantRate = getShippingDominantRate(cartItems.map(it => ({
                      net: (Number(it.netPrice) || 0) * (Number(it.quantity) || 1),
                      vat_rate: Number(it.vatRate) || 0,
                    })));
                    const gf = !vatExclusive && dominantRate > 0 ? 1 + dominantRate / 100 : 1;
                    const gross = (net) => Math.round((Number(net) || 0) * gf * 100) / 100;
                    const grossDiscounts = vatExclusive ? couponDiscounts : couponDiscounts.map(d => ({ ...d, computed_discount: gross(d.computed_discount) }));
                    return (
                      <PriceBreakdown
                        t={t}
                        couponDiscounts={grossDiscounts}
                        ruleDiscount={gross(ruleDiscount)}
                        ruleDiscountLabel={ruleDiscountDescriptor || undefined}
                        hideShipping
                        shippingCost={gross(shippingCost)}
                        shippingFree={shippingFree}
                        volumeDiscount={gross(volumeDiscount)}
                        volumeDiscountPercentage={volumeDiscountPercentage}
                        appliedBalance={appliedBalance}
                        roundOffDiff={itemsRoundOffDiff}
                        vatExclusive={vatExclusive}
                        netSubtotal={vatExclusive ? netSubtotal : gross(netSubtotal)}
                        vatOnDiscount={vatOnDiscount}
                        vatAmount={vatExclusive ? itemVat : undefined}
                      />
                    );
                  })()}

                  <Divider my="sm" />

                  <Group justify="space-between" wrap="nowrap">
                    <Text size="md" fw={900}>
                      {Math.round(itemsRoundOffDiff * 100) !== 0
                        ? `${t("totalPayable", "Total payable")} *`
                        : t("total", "Total")}{" "}
                      {vatExclusive ? "" : `(${t("inclVAT", "incl. VAT")}):`}
                    </Text>
                    <Text size={rem(24)} fw={900} color="blue.7" style={{ flexShrink: 0 }}>
                      {formatPrice(itemsTotal)}
                    </Text>
                  </Group>
                  {!vatExclusive && (
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="xs" c="dimmed">
                        ** {t("vat", "VAT")}:
                      </Text>
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{formatPrice(itemVat)}</Text>
                    </Group>
                  )}
                  {Math.round(itemsRoundOffDiff * 100) !== 0 && (
                    <Text size="xs" c="dimmed" role="note">
                      * {t("roundingNote", "Totals are rounded to the nearest CHF 0.05.")}
                    </Text>
                  )}
                </Stack>

                <Button
                  fullWidth
                  size="xl"
                  radius="xl"
                  mt={rem(30)}
                  color="blue"
                  onClick={() => navigate("/checkout/confirm")}
                >
                  {t("proceedToCheckout", "Proceed to Checkout")}
                </Button>

                <Button
                  fullWidth
                  variant="subtle"
                  color="gray"
                  mt="sm"
                  component={Link}
                  to="/products"
                >
                  {t("continueShopping", "Continue Shopping")}
                </Button>
              </Paper>

              {/* COUPON SECTION */}
              <Paper p="lg" radius="lg" withBorder shadow="xs">
                <Text fw={700} size="xs" mb="sm" tt="uppercase" c="dimmed">
                  {t("applyCoupon")}
                </Text>
                <Group gap="xs">
                  <label htmlFor="coupon-code-input" className="sr-only">
                    {t("enterCoupon", "Enter coupon code")}
                  </label>
                  <TextInput
                    id="coupon-code-input"
                    placeholder={t("enterCoupon", "Enter code")}
                    flex={1}
                    radius="md"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    aria-label={t("enterCoupon", "Enter coupon code")}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button
                    variant="filled"
                    color="blue"
                    radius="md"
                    loading={isApplying}
                    onClick={handleApplyCoupon}
                  >
                    {t("applyCoupon", "Apply")}
                  </Button>
                </Group>
                {couponFeedback && (
                  <Text
                    size="xs"
                    mt="xs"
                    c={couponFeedback.type === "success" ? "green.7" : "red.7"}
                    fw={700}
                    role={couponFeedback.type === "error" ? "alert" : "status"}
                    aria-live="polite"
                  >
                    {t(couponFeedback.key)}
                  </Text>
                )}
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
    </Box>
  );
}
